import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import LoadingState from "@/components/base/LoadingState";
import StatusBadge from "@/components/base/StatusBadge";
import JobModal, { type Job } from "@/pages/admin/jobs/components/JobModal";
import OverviewTab from "./tabs/OverviewTab";
import WorkflowTab from "./tabs/WorkflowTab";
import PhotosTab from "./tabs/PhotosTab";
import DeliveriesTab from "./tabs/DeliveriesTab";
import MediaPackageTab from "./tabs/MediaPackageTab";
import MessagesTab from "./tabs/MessagesTab";
import NotesTab from "./tabs/NotesTab";
import ActivityTab from "./tabs/ActivityTab";
import EventRoutesTab from "./tabs/EventRoutesTab";
import { logActivity } from "@/lib/logActivity";
import Swal from "sweetalert2";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { queueEmail, deliveryReadyBody } from "@/lib/queueEmail";
import { createNotification } from "@/lib/createNotification";
import { WORKFLOW_STATUSES, getWorkflowStatus, getTimestampsForStatus, PIPELINE_STEPS, type WorkflowStatus } from "@/lib/workflowStatus";

type Tab = "overview" | "workflow" | "photos" | "deliveries" | "media" | "messages" | "notes" | "activity" | "routes";

const ALL_TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "overview",   label: "Overview",       icon: "ri-layout-grid-line" },
  { id: "workflow",   label: "Workflow",        icon: "ri-git-branch-line" },
  { id: "photos",     label: "Photos",          icon: "ri-image-2-line" },
  { id: "deliveries", label: "Deliveries",      icon: "ri-send-plane-line" },
  { id: "media",      label: "Media Package",   icon: "ri-disc-line" },
  { id: "messages",   label: "Messages",        icon: "ri-message-3-line" },
  { id: "routes",     label: "Event Routes",    icon: "ri-route-line" },
  { id: "notes",      label: "Notes",           icon: "ri-sticky-note-2-line" },
  { id: "activity",   label: "Activity",        icon: "ri-history-line" },
];

export default function JobDetailPage() {
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [clients, setClients] = useState<{ id: string; full_name: string }[]>([]);
  const [contracts, setContracts] = useState<{ id: string; title: string; contract_number: string; total_amount: number; balance_amount: number; status: string; payment_status?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [workflowProgress, setWorkflowProgress] = useState<{ completed: number; total: number } | null>(null);
  const [noteCount, setNoteCount] = useState(0);

  const { checking: checkingPhotoModule, hasAccess: photoModuleActive } = useModuleAccess('photo_selection');
  const { checking: checkingDeliveryModule, hasAccess: deliveryModuleActive } = useModuleAccess('final_deliveries');
  const { checking: checkingMediaModule, hasAccess: mediaModuleActive } = useModuleAccess('media_packages');

  const visibleTabs = useMemo(() => {
    let tabs = ALL_TABS;
    if (!checkingPhotoModule && !photoModuleActive) {
      tabs = tabs.filter((t) => t.id !== "photos");
    }
    if (!checkingDeliveryModule && !deliveryModuleActive) {
      tabs = tabs.filter((t) => t.id !== "deliveries");
    }
    if (!checkingMediaModule && !mediaModuleActive) {
      tabs = tabs.filter((t) => t.id !== "media");
    }
    return tabs;
  }, [checkingPhotoModule, photoModuleActive, checkingDeliveryModule, deliveryModuleActive, checkingMediaModule, mediaModuleActive]);

  useEffect(() => {
    if (!checkingPhotoModule && !photoModuleActive && activeTab === "photos") {
      setActiveTab("overview");
    }
    if (!checkingDeliveryModule && !deliveryModuleActive && activeTab === "deliveries") {
      setActiveTab("overview");
    }
    if (!checkingMediaModule && !mediaModuleActive && activeTab === "media") {
      setActiveTab("overview");
    }
  }, [checkingPhotoModule, photoModuleActive, checkingDeliveryModule, deliveryModuleActive, checkingMediaModule, mediaModuleActive, activeTab]);

  const fetchJob = useCallback(async (tid: string, jid: string) => {
    setLoading(true);
    const [jobRes, clientsRes, contractsRes] = await Promise.all([
      supabase.from("jobs").select("*").eq("id", jid).eq("tenant_id", tid).maybeSingle(),
      supabase.from("clients").select("id, full_name, email").eq("tenant_id", tid).eq("status", "active"),
      supabase.from("contracts").select("id, title, contract_number, total_amount, balance_amount, status, payment_status").eq("tenant_id", tid),
    ]);
    if (jobRes.error || !jobRes.data) { setNotFound(true); setLoading(false); return; }
    const jobData = jobRes.data as Job;
    setJob(jobData);
    if (!clientsRes.error && clientsRes.data) setClients(clientsRes.data as { id: string; full_name: string }[]);
    if (!contractsRes.error && contractsRes.data) setContracts(contractsRes.data as typeof contracts);

    // Fetch unread messages count
    const { count } = await supabase
      .from("client_messages")
      .select("id", { count: "exact", head: true })
      .eq("client_id", jobData.client_id)
      .eq("tenant_id", tid)
      .eq("sender_type", "client")
      .eq("is_read", false);
    setUnreadMessages(count ?? 0);

    // Fetch workflow progress
    const { data: steps } = await supabase
      .from("job_steps")
      .select("status")
      .eq("job_id", jid)
      .eq("tenant_id", tid);
    if (steps) {
      setWorkflowProgress({ completed: steps.filter((s: { status: string }) => s.status === "completed").length, total: steps.length });
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid && jobId) fetchJob(tid, jobId);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchJob, jobId]);

  const handleSaveEdit = async (data: Job) => {
    if (!tenantId || !job) return;
    const now = new Date().toISOString();
    const wfStatus = (data.workflow_status || data.status) as WorkflowStatus;
    const wfTimestamps = getTimestampsForStatus(wfStatus, now);
    const { data: updated, error } = await supabase
      .from("jobs")
      .update({
        title: data.title,
        client_id: data.client_id,
        contract_id: data.contract_id || null,
        job_type: data.job_type,
        status: data.status,
        workflow_status: wfStatus,
        shoot_date: data.shoot_date || null,
        location: data.location || null,
        notes: data.notes || null,
        updated_at: now,
        ...wfTimestamps,
      })
      .eq("id", job.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
      return;
    }
    if (updated) {
      setJob(updated as Job);
      logActivity({ tenantId, action: "updated", entity: "job", entityId: job.id, entityName: data.title });
    }
    setEditModalOpen(false);
  };

  const handleWorkflowStatusChange = async (newStatus: WorkflowStatus) => {
    if (!tenantId || !job) return;
    const cfg = getWorkflowStatus(newStatus);
    const result = await Swal.fire({
      title: `Move to "${cfg.label}"?`,
      text: cfg.description,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Set ${cfg.label}`,
      cancelButtonText: "Cancel",
      background: "#fff",
      color: "#1e293b",
      confirmButtonColor: "#C8A96E",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed) return;
    const now = new Date().toISOString();
    const timestamps = getTimestampsForStatus(newStatus, now);
    const { data: updated, error } = await supabase
      .from("jobs")
      .update({ workflow_status: newStatus, status: newStatus, updated_at: now, ...timestamps })
      .eq("id", job.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#1e293b" });
      return;
    }
    if (updated) {
      setJob(updated as Job);
      createNotification({ tenantId, type: "info", title: "Workflow updated", message: `"${job.title}" moved to ${cfg.label}.` });
      logActivity({ tenantId, action: "updated", entity: "job", entityId: job.id, entityName: job.title });
    }
  };

  if (loading) return <AdminLayout><LoadingState /></AdminLayout>;

  if (notFound || !job) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-slate-100">
            <i className="ri-camera-3-line text-slate-300 text-3xl"></i>
          </div>
          <div className="text-center">
            <p className="text-[#0f172a] font-semibold text-lg mb-1">Job not found</p>
            <p className="text-slate-400 text-sm">This job doesn&apos;t exist or you don&apos;t have access.</p>
          </div>
          <button onClick={() => navigate("/admin/jobs")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line"></i>
            Back to Jobs
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page header */}
      <div className="px-4 md:px-8 pt-6 pb-0">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
          <button onClick={() => navigate("/admin/jobs")} className="hover:text-slate-600 transition-colors cursor-pointer">Jobs</button>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-slate-500 truncate max-w-[200px]">{job.title}</span>
        </div>

        {/* Title row */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-start gap-4">
            <button onClick={() => navigate("/admin/jobs")} className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-400 hover:text-[#0f172a] transition-all cursor-pointer flex-shrink-0 mt-0.5">
              <i className="ri-arrow-left-line text-sm"></i>
            </button>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-[#0f172a] font-bold text-xl leading-tight">{job.title}</h1>
                {/* Delivery status badge */}
                {job.delivery_status && job.delivery_status !== "not_started" && (
                  <StatusBadge status={job.delivery_status} size="sm" />
                )}
                {/* Photo selection status badge */}
                {job.photo_selection_status && job.photo_selection_status !== "not_started" && (
                  <span className={`
                    text-[11px] font-semibold px-2.5 py-0.5 rounded-full border capitalize whitespace-nowrap
                    ${job.photo_selection_status === "submitted"
                      ? "bg-amber-50 border-amber-200 text-amber-600"
                      : job.photo_selection_status === "reviewed"
                        ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                        : job.photo_selection_status === "in_progress"
                          ? "bg-blue-50 border-blue-200 text-blue-600"
                          : "bg-slate-50 border-slate-200 text-slate-500"}
                  `}>
                    <span className="flex items-center gap-1">
                      <i className={
                        job.photo_selection_status === "submitted" ? "ri-time-line" :
                        job.photo_selection_status === "reviewed" ? "ri-check-double-line" :
                        job.photo_selection_status === "in_progress" ? "ri-loader-4-line animate-spin" :
                        "ri-image-2-line"
                      }></i>
                      {job.photo_selection_status.replace(/_/g, " ")}
                    </span>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                <span className="text-slate-400 text-sm">{job.job_type}</span>
                {job.shoot_date && (
                  <>
                    <span className="text-slate-200">·</span>
                    <span className="text-slate-400 text-sm">
                      {new Date(job.shoot_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  </>
                )}
                {job.location && (
                  <>
                    <span className="text-slate-200">·</span>
                    <span className="text-slate-400 text-sm flex items-center gap-1">
                      <i className="ri-map-pin-line text-xs"></i>
                      {job.location}
                    </span>
                  </>
                )}
                {workflowProgress && workflowProgress.total > 0 && (
                  <>
                    <span className="text-slate-200">·</span>
                    <span className="text-[#C8A96E] text-sm font-medium">
                      {workflowProgress.completed}/{workflowProgress.total} steps
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Mark Delivered button */}
            {job.delivery_status && job.delivery_status !== "not_started" && (
              <button
                onClick={async () => {
                  const isDelivered = job.delivery_status === "delivered";
                  const result = await Swal.fire({
                    title: isDelivered ? "Unmark as delivered?" : "Mark as delivered?",
                    text: isDelivered
                      ? "This will reset the delivery status to reflect current deliveries."
                      : "This will mark this job as delivered to the client.",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: isDelivered ? "Unmark" : "Mark Delivered",
                    cancelButtonText: "Cancel",
                    background: "#fff",
                    color: "#1e293b",
                    confirmButtonColor: isDelivered ? "#94a3b8" : "#10b981",
                    cancelButtonColor: "#94a3b8",
                  });
                  if (!result.isConfirmed || !tenantId) return;
                  const now = new Date().toISOString();
                  const updatePayload: Record<string, unknown> = {
                    delivery_status: isDelivered ? "prepared" : "delivered",
                    updated_at: now,
                  };
                  if (!isDelivered) updatePayload.delivered_at = now;
                  else updatePayload.delivered_at = null;
                  const { data: updated, error } = await supabase
                    .from("jobs")
                    .update(updatePayload)
                    .eq("id", job.id)
                    .eq("tenant_id", tenantId)
                    .select()
                    .maybeSingle();
                  if (error) {
                    Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#1e293b" });
                  } else if (updated) {
                    setJob(updated as Job);
                    if (!isDelivered && job.client_id) {
                      const [{ data: client }, { data: tokenRow }] = await Promise.all([
                        supabase
                          .from("clients")
                          .select("email, full_name")
                          .eq("id", job.client_id)
                          .eq("tenant_id", tenantId)
                          .maybeSingle(),
                        supabase
                          .from("client_access_tokens")
                          .select("token")
                          .eq("client_id", job.client_id)
                          .eq("tenant_id", tenantId)
                          .eq("status", "active")
                          .order("created_at", { ascending: false })
                          .limit(1)
                          .maybeSingle(),
                      ]);
                      if (client?.email) {
                        const portalUrl = tokenRow?.token
                          ? `${window.location.origin}/portal/${tokenRow.token}`
                          : `${window.location.origin}/portal`;
                        queueEmail({
                          tenantId,
                          toEmail: client.email,
                          subject: "Your files are ready",
                          body: deliveryReadyBody({
                            clientName: client.full_name || "Client",
                            jobTitle: job.title,
                            portalUrl,
                          }),
                        });
                      }
                      createNotification({
                        tenantId,
                        type: "success",
                        title: "Delivery marked as completed",
                        message: `Job "${job.title}" has been marked as delivered.`,
                      });
                    }
                    Swal.fire({
                      title: isDelivered ? "Unmarked" : "Marked Delivered!",
                      text: isDelivered
                        ? "Job delivery status reset."
                        : "Job marked as delivered to the client.",
                      icon: "success",
                      timer: 2000,
                      showConfirmButton: false,
                      background: "#fff",
                      color: "#1e293b",
                      iconColor: "#C8A96E",
                    });
                  }
                }}
                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap border ${
                  job.delivery_status === "delivered"
                    ? "text-slate-500 border-slate-200 hover:bg-slate-50"
                    : "text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                }`}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={job.delivery_status === "delivered" ? "ri-arrow-go-back-line" : "ri-check-double-line"}></i>
                </span>
                {job.delivery_status === "delivered" ? "Unmark Delivered" : "Mark Delivered"}
              </button>
            )}
            {/* Mark Reviewed button */}
            {job.photo_selection_status === "submitted" && (
              <button
                onClick={async () => {
                  const result = await Swal.fire({
                    title: "Mark selection as reviewed?",
                    text: "The client will see this job's photo selection as reviewed.",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: "Mark Reviewed",
                    cancelButtonText: "Cancel",
                    background: "#fff",
                    color: "#1e293b",
                    confirmButtonColor: "#C8A96E",
                    cancelButtonColor: "#94a3b8",
                  });
                  if (!result.isConfirmed || !tenantId) return;
                  const { data: updated, error } = await supabase
                    .from("jobs")
                    .update({ photo_selection_status: "reviewed", updated_at: new Date().toISOString() })
                    .eq("id", job.id)
                    .eq("tenant_id", tenantId)
                    .select()
                    .maybeSingle();
                  if (error) {
                    Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#1e293b" });
                  } else if (updated) {
                    setJob(updated as Job);
                    Swal.fire({
                      title: "Reviewed!",
                      text: "Photo selection marked as reviewed.",
                      icon: "success",
                      timer: 2000,
                      showConfirmButton: false,
                      background: "#fff",
                      color: "#1e293b",
                      iconColor: "#C8A96E",
                    });
                  }
                }}
                className="flex items-center gap-1.5 bg-white border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-emerald-600 text-xs font-medium px-3 py-2 rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-double-line"></i>
                Mark Reviewed
              </button>
            )}
            <StatusBadge status={job.status} />
            <button
              onClick={() => setEditModalOpen(true)}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0f172a] bg-slate-50 hover:bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-pencil-line"></i>
              Edit Job
            </button>
          </div>
        </div>

        {/* Workflow Pipeline Bar */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-slate-400 text-xs uppercase tracking-wider">Workflow Status</p>
            {job.workflow_status === "cancelled" && (
              <span className="text-red-400 text-xs font-medium flex items-center gap-1">
                <i className="ri-close-circle-line"></i>
                Cancelled
              </span>
            )}
          </div>
          {/* Pipeline progress steps */}
          <div className="flex items-center gap-1 overflow-x-auto pb-1">
            {PIPELINE_STEPS.map((step, idx) => {
              const cfg = getWorkflowStatus(step);
              const currentIdx = PIPELINE_STEPS.indexOf((job.workflow_status || job.status) as WorkflowStatus);
              const isActive = step === (job.workflow_status || job.status);
              const isDone = idx < currentIdx;
              const isCancelled = job.workflow_status === "cancelled" || job.status === "cancelled";
              return (
                <button
                  key={step}
                  onClick={() => !isActive && handleWorkflowStatusChange(step)}
                  disabled={isActive}
                  title={cfg.description}
                  className={`
                    flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 border
                    ${
                      isCancelled
                        ? "opacity-30 bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
                        : isActive
                          ? `${cfg.bg} ${cfg.border} ${cfg.color} cursor-default ring-1 ring-offset-1 ring-[#C8A96E]/30`
                          : isDone
                            ? "bg-emerald-50 border-emerald-200 text-emerald-500 cursor-pointer hover:bg-emerald-100"
                            : "bg-slate-50 border-slate-200 text-slate-400 cursor-pointer hover:bg-slate-100 hover:text-slate-600"
                    }
                  `}
                >
                  {isDone && !isCancelled ? (
                    <i className="ri-check-line text-xs"></i>
                  ) : (
                    <i className={`${cfg.icon} text-xs`}></i>
                  )}
                  <span className="hidden sm:inline">{cfg.shortLabel}</span>
                  <span className="sm:hidden">{idx + 1}</span>
                </button>
              );
            })}
            {/* Cancelled quick action */}
            {job.workflow_status !== "cancelled" && job.status !== "cancelled" && (
              <button
                onClick={() => handleWorkflowStatusChange("cancelled")}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 border bg-slate-50 border-slate-200 text-slate-400 hover:bg-red-50 hover:border-red-200 hover:text-red-500 cursor-pointer ml-1"
                title="Cancel this job"
              >
                <i className="ri-close-circle-line text-xs"></i>
                <span className="hidden sm:inline">Cancel</span>
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all cursor-pointer whitespace-nowrap border-b-2 -mb-px flex-shrink-0 ${
                activeTab === tab.id ? "text-[#C8A96E] border-[#C8A96E]" : "text-slate-400 border-transparent hover:text-slate-600"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={tab.icon}></i>
              </span>
              {tab.label}
              {/* Badges */}
              {tab.id === "messages" && unreadMessages > 0 && (
                <span className="bg-[#C8A96E] text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {unreadMessages}
                </span>
              )}
              {tab.id === "workflow" && workflowProgress && workflowProgress.total > 0 && workflowProgress.completed === workflowProgress.total && (
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              )}
              {tab.id === "notes" && noteCount > 0 && (
                <span className="bg-slate-200 text-slate-500 text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {noteCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="px-4 md:px-8 py-6">
        {activeTab === "overview" && tenantId && <OverviewTab job={job} tenantId={tenantId} />}
        {activeTab === "workflow" && tenantId && <WorkflowTab job={job} tenantId={tenantId} />}
        {activeTab === "photos" && tenantId && <PhotosTab job={job} tenantId={tenantId} />}
        {activeTab === "deliveries" && tenantId && (
          <DeliveriesTab
            job={job}
            tenantId={tenantId}
            onJobUpdated={(upd) => setJob((prev) => prev ? { ...prev, ...upd } : prev)}
            clientEmail={clients.find((c) => c.id === job.client_id)?.email}
            clientName={clients.find((c) => c.id === job.client_id)?.full_name}
          />
        )}
        {activeTab === "media" && tenantId && <MediaPackageTab job={job} tenantId={tenantId} />}
        {activeTab === "messages" && tenantId && <MessagesTab job={job} tenantId={tenantId} />}
        {activeTab === "routes" && tenantId && <EventRoutesTab job={job} tenantId={tenantId} />}
        {activeTab === "notes" && tenantId && <NotesTab job={job} tenantId={tenantId} onNoteCountChange={setNoteCount} />}
        {activeTab === "activity" && tenantId && <ActivityTab job={job} tenantId={tenantId} />}
      </div>

      {/* Edit modal */}
      {editModalOpen && tenantId && (
        <JobModal
          job={job}
          isNew={false}
          clients={clients}
          contracts={contracts}
          onClose={() => setEditModalOpen(false)}
          onSave={handleSaveEdit}
        />
      )}
    </AdminLayout>
  );
}